import React from 'react';

import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogProps,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { CloseRounded, SaveRounded, SearchRounded } from '@material-ui/icons';
import { SnackbarProvider, useSnackbar } from 'notistack';

import Gallery from 'react-photo-gallery';

import { FileSelectorTile } from './FileTile';
import { FileSelectorDrop } from './FileUpload';
import { RenameFile } from './FileRename';
import { FileSelectorExpandedFile } from './FileExpanded';

import { FileSelectorData } from './FileSelectorContext';

import {
  ExpandedFileFileResponse,
  ExtendedInputPhotoProps,
  ExtendedRenderInputImageProps,
} from './index.private.interfaces';

import { fileFetch, fileRemove, fileRename, fileUpload } from './index.functions';
import { ScrollToTop } from './HelperComponents';
import { useInView } from 'react-intersection-observer';

export interface FileSelectorProps {
  links: {
    get?: FileSelectorPropsLinks;
    upload?: FileSelectorPropsLinks;
    delete?: FileSelectorPropsLinks;
    rename?: FileSelectorPropsLinks;
    root?: Link;
  } & Omit<FileSelectorPropsLinks, 'method'>;
  callback?: (files: FileResponse[]) => void;
  callbackDone?: (files: FileResponse[]) => void;
  filter?: (files: FileResponse[]) => FileResponse[];
  previewImage?: (path: string) => string;
  pickedFiles?: Link[];
  multiple?: boolean;
  size?: number;
  useDescription?: boolean;
}

export interface FileSelectorRawProps extends Omit<FileSelectorProps, 'useDescription'> {
  files: FileResponse[];
  loading: boolean;
  selectedFiles: FileResponse[];
  setFiles: React.Dispatch<React.SetStateAction<FileResponse[]>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileResponse[]>>;
  scrollRef?: (node: HTMLElement | null) => void;
  useDescription: boolean;
}

export interface FileSelectorPropsLinks {
  method?: HTTPMethod;
  url?: Link;
  settings?: FileSelectorLinkSettings;
}
export interface FileResponse {
  path: Link;
  displayName?: string;
  filetype: 'image' | 'document' | 'pdf' | 'binary' | 'video' | 'vectorgraphic';
  width?: number;
  height?: number;
}

export type Link = string;
export type HTTPMethod = string;
export type FileSelectorLinkSettings = Omit<RequestInit, 'method'> & {
  url?: string;
  method?: string;
};

const fileSelector = {
  fileSelector: {
    flexGrow: 1,
    background: 'green',
  },
  closeButton: {
    marginRight: '16px',
  },
  title: {
    marginRight: '16px',
  },
  fileSelectorDialog: {
    borderRadius: `0 0 8px 8px`,
    margin: '40px',
    height: `calc(100% - 80px)`,
  },
  search: {
    marginLeft: '8px',
    width: '15rem',
    transition: 'width 0.2s ease-in-out',
    '&:focusWithin': {
      width: '20rem',
    },
    color: '#fff',
  },
  saveButton: {
    flexShrink: 0,
    color: '#fff',
  },
  contrastText: {
    color: '#fff',
  },
} as const;

FileSelector.defaultProps = {
  useDescription: true,
};

export function FileSelector(props: FileSelectorProps & DialogProps) {
  const {
    links,
    callback,
    callbackDone,
    filter,
    previewImage,
    pickedFiles,
    multiple,
    size,
    useDescription,
    ...dialogProps
  } = props;

  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const [files, setFiles] = React.useState<FileResponse[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedFiles, setSelectedFiles] = React.useState<FileResponse[]>([]);

  const [search, setSearch] = React.useState('');

  const [scrollTarget, setScrollTarget] = React.useState<HTMLElement>();

  return (
    <SnackbarProvider preventDuplicate maxSnack={3}>
      <Dialog
        {...dialogProps}
        PaperProps={{
          style: fileSelector.fileSelectorDialog,
        }}
        fullScreen
      >
        <AppBar position="sticky">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              style={fileSelector.closeButton}
              onClick={(event) => dialogProps.onClose?.(event, 'escapeKeyDown')}
              aria-label="close"
            >
              <CloseRounded />
            </IconButton>

            {matches && (
              <Typography variant="h6" style={fileSelector.title}>
                Dateiauswahl
              </Typography>
            )}
            {useDescription && (
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Suche"
                style={fileSelector.search}
                variant="filled"
                size="small"
                hiddenLabel
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded style={fileSelector.contrastText} />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                  style: fileSelector.contrastText,
                }}
              />
            )}
            <Box flexGrow={1} />

            <Tooltip title="Speichern">
              <IconButton onClick={() => props.callbackDone?.(selectedFiles)} style={fileSelector.saveButton}>
                <SaveRounded color="inherit" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <FileSelectorRaw
          {...{
            links: { root: '', ...links },
            callback,
            callbackDone,
            previewImage,
            pickedFiles,
            multiple,
            size,
            files,
            loading,
            selectedFiles,
            setFiles,
            setLoading,
            setSelectedFiles,
            useDescription: useDescription !== false,
          }}
          filter={(files) => {
            const filteredFiles = filter?.(files) || files;
            const searchedFiles = useDescription
              ? filteredFiles.filter((files) =>
                  (files.displayName as string).toLowerCase().includes(search.toLowerCase()),
                )
              : filteredFiles;
            return searchedFiles.length ? searchedFiles : filteredFiles;
          }}
          scrollRef={(node: HTMLElement | null) => node && setScrollTarget(node)}
        />

        <ScrollToTop scrollTarget={scrollTarget} />
      </Dialog>
    </SnackbarProvider>
  );
}

const fileSelectorRaw = {
  fileSelectorRawWrapper: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  fileSelectorRaw: {
    overflow: 'auto',
    width: '100%',
  },
  loadingAnimation: {
    alignSelf: 'center',
  },
} as const;

const uploadTile: ExtendedInputPhotoProps = {
  src: '',
  filetype: 'upload',
  width: 1,
  height: 1,
};

const spacerTiles: ExtendedInputPhotoProps[] = Array(10)
  .fill(null)
  .map(
    (_, i) =>
      ({
        src: '',
        filetype: 'spacer',
        key: i + 'spacer',
        width: 1,
        height: 1,
      } as ExtendedInputPhotoProps),
  );

FileSelectorRaw.defaultProps = {
  pickedFiles: [],
  multiple: false,
  size: 200,
};

export function FileSelectorRaw(props: FileSelectorRawProps) {
  // Abort fetch requests on unmount
  const abortController = React.useRef(new AbortController());
  const snackbar = useSnackbar();

  const { ref, inView } = useInView();

  const [expandedFile, setExpandedFile] = React.useState<FileResponse | undefined>(undefined);

  const [renameFile, setRenameFile] = React.useState<FileResponse | undefined>(undefined);
  const [renameFiles, setRenameFiles] = React.useState<FileResponse[]>([]);

  const [dropHover, setDropHover] = React.useState(false);

  const [uploadCount, setUploadCount] = React.useState(0 as number);

  const initialSelectedFiles = React.useRef(props.pickedFiles);

  React.useEffect(() => {
    if (!renameFile && renameFiles.length) {
      const newRenameFiles = [...renameFiles];

      setRenameFile(newRenameFiles.shift());
      setRenameFiles(newRenameFiles);
    }
  }, [renameFile, renameFiles]);

  React.useEffect(
    () =>
      props.setSelectedFiles(() => {
        // Apply selected files by path when pickedFiles changes
        const selectedFiles = props.files.filter((file: FileResponse) =>
          props.pickedFiles?.some((pickedFile) => pickedFile.includes(file.path)),
        );

        return selectedFiles.slice(0, props.multiple ? undefined : 1);
      }),
    //eslint-disable-next-line
    [props.pickedFiles],
  );

  React.useEffect(
    () => props.callback?.(props.selectedFiles),
    //eslint-disable-next-line
    [props.selectedFiles],
  );

  React.useEffect(() => {
    fileFetch(props, abortController.current, snackbar)
      // First file filtering for picked files
      .then((files) => {
        if (files)
          props.setSelectedFiles(
            files
              .filter((file: FileResponse) => props.pickedFiles?.some((pickedFile) => pickedFile.includes(file.path)))
              .slice(0, props.multiple ? undefined : 1),
          );
      });
    const fetchInterval = setInterval(() => fileFetch(props, abortController.current, snackbar), 60 * 1000);

    return () => {
      // Abort and cancel all current fetches
      //eslint-disable-next-line
      abortController.current.abort();
      clearInterval(fetchInterval);
    };
    //eslint-disable-next-line
  }, []);

  // Filter and sort displayed files
  const filteredFiles = (props.filter?.(props.files) || props.files).sort(
    (fileA, fileB) =>
      (initialSelectedFiles.current?.some((path) => path.includes(fileB.path)) ? 1 : 0) -
      (initialSelectedFiles.current?.some((path) => path.includes(fileA.path)) ? 1 : 0),
  );

  const [displayedFilesLength, increaseDisplayedFilesLength] = React.useReducer((state) => state + 40, 80);

  React.useEffect(() => {
    if (inView) increaseDisplayedFilesLength();
  }, [inView]);

  const photos = React.useMemo<ExtendedInputPhotoProps[]>(() => {
    return [
      // Add upload tile if upload is enabled
      ...(props.links.upload || props.links.settings ? [uploadTile] : []),

      ...filteredFiles.slice(0, displayedFilesLength).map((file) => ({
        src: file.path,
        path: file.path,
        displayName: file.displayName,
        filetype: file.filetype,
        width: file.width || 1,
        height: file.height || 1,
      })),
      // Add spacers to reduce height when there are no or less files
      ...spacerTiles.slice(filteredFiles.length),
    ];
  }, [props.links.upload, props.links.settings, filteredFiles, displayedFilesLength]);

  const renderImage = (imageProps: ExtendedRenderInputImageProps) => (
    <FileSelectorTile
      key={imageProps.photo.src}
      {...{
        ...imageProps,
        selected: props.selectedFiles.some((selected) => selected.path.includes(imageProps.photo.src)),
        mutliple: props.multiple,
      }}
    />
  );

  return (
    <FileSelectorData.Provider
      value={{
        rootPath: props.links.root,
        previewImage: props.previewImage,
        multiple: props.multiple || false,
        uploading: Boolean(uploadCount),
        setSelected: props.setSelectedFiles,
        onUpload: (files) =>
          fileUpload(
            props,
            abortController.current,
            snackbar,
            files,
            setUploadCount,
            props.useDescription ? setRenameFiles : () => {},
          ),
        onRename: (file) => fileRename(props, abortController.current, snackbar, file),
        onRemove: (file) => fileRemove(props, abortController.current, snackbar, file),
        onMaximize: setExpandedFile,
        removable: !props.links.delete || !props.links.settings,
        renamable: props.useDescription && (!props.links.rename || !props.links.settings),
        size: props.size as number,
        useDescription: props.useDescription,
      }}
    >
      <FileSelectorExpandedFile file={expandedFile as ExpandedFileFileResponse} onClose={setExpandedFile} />

      {props.useDescription && (
        <RenameFile
          file={renameFile}
          onRename={async (file) => {
            const success = await fileRename(props, abortController.current, snackbar, file);
            if (success) setRenameFile(undefined);
          }}
        />
      )}

      <Box sx={fileSelectorRaw.fileSelectorRawWrapper}>
        <FileSelectorDrop open={dropHover} onClose={() => setDropHover(false)} />

        {props.files.length || !props.loading ? (
          <div
            style={{ ...fileSelectorRaw.fileSelectorRaw, pointerEvents: dropHover ? 'none' : 'all' }}
            onDragOver={() => setDropHover(true)}
            ref={props.scrollRef}
          >
            <Gallery
              photos={photos}
              renderImage={(img) => renderImage(img as ExtendedRenderInputImageProps)}
              targetRowHeight={props.size}
            />
            <div ref={ref}>
              <Button onClick={increaseDisplayedFilesLength}>Mehr anzeigen</Button>
            </div>
          </div>
        ) : (
          <CircularProgress style={fileSelectorRaw.loadingAnimation} />
        )}
      </Box>
    </FileSelectorData.Provider>
  );
}
